import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Zap } from "lucide-react";
import { toast } from "sonner";
import { getCustomers, getSettings, getLastReading, addBill, addBillsBulk, getBills } from "@/lib/api";
import FilterBar, { useSessionFilters } from "@/components/FilterBar";

const BULK_FILTER_DEFAULTS = {
  society: "all", tower: "all", lastBilled: "all", connectionType: "all",
};

export default function BulkBillGeneration() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [billDate, setBillDate] = useState(new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 15 * 86400000).toISOString().split("T")[0]
  );
  const [entries, setEntries] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [filters, setFilters] = useSessionFilters("bulkbill", BULK_FILTER_DEFAULTS);

  useEffect(() => {
    const load = async () => {
      try {
        const custs = await getCustomers();
        const s = await getSettings();
        const bills = await getBills();
        setCustomers(custs);
        setSettings(s);
        setAllBills(bills);
        const ents = await Promise.all(
          custs.map(async (c) => {
            let lr = c.lastReading || 0;
            try { lr = await getLastReading(c.id); } catch (e) { console.warn(`Failed to get last reading for ${c.id}:`, e.message); }
            const custBills = bills.filter((b) => b.customerId === c.id).sort((a, b) => new Date(b.billDate) - new Date(a.billDate));
            const lastBillDate = custBills.length > 0 ? custBills[0].billDate : null;
            return { customerId: c.id, selected: true, previousReading: lr, currentReading: lr, lastBillDate };
          })
        );
        setEntries(ents);
      } catch (err) {
        toast.error("Failed to load bulk bill data. Please try again.");
      }
    };
    load();
  }, []);

  const societies = useMemo(() => Array.from(new Set(customers.map((c) => c.society).filter(Boolean))).sort(), [customers]);
  const towers = useMemo(() => {
    const src = filters.society !== "all" ? customers.filter((c) => c.society === filters.society) : customers;
    return Array.from(new Set(src.map((c) => c.towerNo).filter(Boolean))).sort();
  }, [customers, filters.society]);

  const filterConfig = useMemo(() => [
    { key: "society", label: "Society", type: "select", options: [{ value: "all", label: "All Societies" }, ...societies.map((s) => ({ value: s, label: s }))] },
    { key: "tower", label: "Tower", type: "select", options: [{ value: "all", label: "All Towers" }, ...towers.map((t) => ({ value: t, label: t }))] },
    { key: "connectionType", label: "Connection", type: "select", options: [{ value: "all", label: "All Types" }, { value: "PNG", label: "PNG" }, { value: "LPG", label: "LPG" }] },
    { key: "lastBilled", label: "Last Billed", type: "select", options: [{ value: "all", label: "All" }, { value: "this_month", label: "This Month" }, { value: "last_month", label: "Last Month" }, { value: "never", label: "Never Billed" }] },
  ], [societies, towers]);

  const filteredEntries = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, "0")}`;
    return entries.filter((entry) => {
      const c = customers.find((cu) => cu.id === entry.customerId);
      if (!c) return false;
      if (filters.society !== "all" && c.society !== filters.society) return false;
      if (filters.tower !== "all" && c.towerNo !== filters.tower) return false;
      if (filters.connectionType !== "all" && c.connectionType !== filters.connectionType) return false;
      if (filters.lastBilled !== "all") {
        if (filters.lastBilled === "never" && entry.lastBillDate) return false;
        if (filters.lastBilled === "this_month" && (!entry.lastBillDate || !entry.lastBillDate.startsWith(thisMonth))) return false;
        if (filters.lastBilled === "last_month" && (!entry.lastBillDate || !entry.lastBillDate.startsWith(lastMonth))) return false;
      }
      return true;
    });
  }, [entries, customers, filters]);

  const updateEntry = (customerId, field, value) => {
    setEntries((prev) => prev.map((e) => e.customerId === customerId ? { ...e, [field]: value } : e));
  };

  const toggleAll = (checked) => {
    const visibleIds = new Set(filteredEntries.map((e) => e.customerId));
    setEntries((prev) => prev.map((e) => visibleIds.has(e.customerId) ? { ...e, selected: !!checked } : e));
  };

  const allSelected = filteredEntries.length > 0 && filteredEntries.every((e) => e.selected);

  const validEntries = filteredEntries.filter(
    (e) => e.selected && e.currentReading > e.previousReading
  );

  const totalNetPayable = validEntries.reduce((sum, entry) => {
    const consumed = entry.currentReading - entry.previousReading;
    const unitCons = consumed * (settings.conversionFactor || 1);
    const usage = unitCons * (settings.rate || 25);
    return sum + usage + (settings.serviceCharges || 50);
  }, 0);

  const handleGenerate = async () => {
    if (validEntries.length === 0) {
      toast.error("No valid entries. Ensure current reading > previous reading for selected customers.");
      return;
    }
    const billsToCreate = validEntries.map((entry) => {
      const customer = customers.find((c) => c.id === entry.customerId);
      if (!customer) return null;
      const consumed = entry.currentReading - entry.previousReading;
      const unitCons = consumed * (settings.conversionFactor || 1);
      const usage = unitCons * (settings.rate || 25);
      const net = usage + (settings.serviceCharges || 50);
      return {
        customerId: customer.id,
        customerName: customer.name,
        consumerNumber: customer.consumerNumber,
        society: customer.society,
        flatNo: customer.flatNo,
        plotNo: customer.plotNo,
        towerNo: customer.towerNo,
        phone: customer.phone,
        connectionType: customer.connectionType,
        billDate,
        dueDate,
        previousReading: entry.previousReading,
        currentReading: entry.currentReading,
        consumedUnits: consumed,
        conversionFactor: settings.conversionFactor || 1,
        unitConsKgScm: unitCons,
        rate: settings.rate || 25,
        usageAmount: usage,
        serviceCharges: settings.serviceCharges || 50,
        netPayable: net,
      };
    }).filter(Boolean);
    await addBillsBulk(billsToCreate);
    toast.success(`${billsToCreate.length} bill${billsToCreate.length !== 1 ? "s" : ""} generated successfully!`);
    navigate("/bills/history");
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="bulk-bill-page">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <Button
          data-testid="bulk-back-button"
          variant="outline"
          onClick={() => navigate("/bills/generate")}
          className="rounded-sm border-zinc-300"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          Bulk Bill Generation
        </h1>
      </div>

      {/* Common Settings */}
      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none mb-6">
        <CardHeader>
          <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
            Common Bill Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label>Bill Date</Label>
              <Input
                data-testid="bulk-bill-date"
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                className="bg-zinc-50 border-zinc-300 rounded-none mt-1"
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                data-testid="bulk-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-zinc-50 border-zinc-300 rounded-none mt-1"
              />
            </div>
            <div>
              <Label>Conv. Factor</Label>
              <Input
                type="number"
                value={settings.conversionFactor || 1}
                disabled
                className="bg-zinc-100 border-zinc-300 rounded-none mt-1 cursor-not-allowed"
              />
            </div>
            <div>
              <Label>Rate/unit</Label>
              <Input
                type="number"
                value={settings.rate || 25}
                disabled
                className="bg-zinc-100 border-zinc-300 rounded-none mt-1 cursor-not-allowed"
              />
            </div>
            <div>
              <Label>Service Charges</Label>
              <Input
                type="number"
                value={settings.serviceCharges || 50}
                disabled
                className="bg-zinc-100 border-zinc-300 rounded-none mt-1 cursor-not-allowed"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <FilterBar
        pageKey="bulkbill"
        defaults={BULK_FILTER_DEFAULTS}
        config={filterConfig}
        filters={filters}
        setFilters={setFilters}
        filteredCount={filteredEntries.length}
        totalCount={entries.length}
      />

      {/* Customer Readings */}
      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
              Customer Readings
            </CardTitle>
            <p className="text-sm text-zinc-500 mt-1">
              {validEntries.length} valid of {filteredEntries.filter((e) => e.selected).length} selected
              {validEntries.length > 0 && (
                <span className="font-mono font-semibold text-orange-600 ml-2">
                  Total: ₹{totalNetPayable.toFixed(2)}
                </span>
              )}
            </p>
          </div>
          <Button
            data-testid="bulk-generate-button"
            onClick={handleGenerate}
            disabled={validEntries.length === 0}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
          >
            <Zap className="w-4 h-4 mr-2" /> Generate {validEntries.length} Bill{validEntries.length !== 1 ? "s" : ""}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="bulk-select-all"
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase">Customer</TableHead>
                  <TableHead className="text-xs font-bold uppercase">Consumer No</TableHead>
                  <TableHead className="text-xs font-bold uppercase">Society</TableHead>
                  <TableHead className="text-xs font-bold uppercase">Last Billed</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Initial Reading</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Prev Reading</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Current Reading</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Consumed</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Net Payable</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-right">Days Since</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-sm text-zinc-400 py-12">
                      No customers match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => {
                    const c = customers.find((cu) => cu.id === entry.customerId);
                    if (!c) return null;
                    const consumed = Math.max(0, entry.currentReading - entry.previousReading);
                    const unitCons = consumed * (settings.conversionFactor || 1);
                    const usage = unitCons * (settings.rate || 25);
                    const net = usage + (settings.serviceCharges || 50);
                    const isValid = consumed > 0;
                    return (
                      <TableRow
                        key={c.id}
                        data-testid={`bulk-row-${c.id}`}
                        className={`hover:bg-zinc-50 ${!entry.selected ? "opacity-40" : ""}`}
                      >
                        <TableCell>
                          <Checkbox
                            data-testid={`bulk-check-${c.id}`}
                            checked={entry.selected}
                            onCheckedChange={(v) => updateEntry(c.id, "selected", !!v)}
                          />
                        </TableCell>
                        <TableCell className="text-sm font-medium">{c.name}</TableCell>
                        <TableCell className="text-sm font-mono">{c.consumerNumber}</TableCell>
                        <TableCell className="text-sm">{c.society}</TableCell>
                        <TableCell className="text-sm">
                          {entry.lastBillDate ? new Date(entry.lastBillDate).toLocaleDateString("en-IN") : "Never"}
                        </TableCell>
                        <TableCell className="text-sm font-mono text-right text-zinc-400">{(c.initialReading || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-sm font-mono text-right">{entry.previousReading.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            data-testid={`bulk-reading-${c.id}`}
                            type="number"
                            value={entry.currentReading}
                            onChange={(e) =>
                              updateEntry(c.id, "currentReading", parseFloat(e.target.value) || 0)
                            }
                            className="w-28 ml-auto bg-zinc-50 border-zinc-300 rounded-none text-right text-sm font-mono"
                          />
                        </TableCell>
                        <TableCell className="text-sm font-mono text-right">
                          {isValid ? consumed.toFixed(2) : "—"}
                        </TableCell>
                        <TableCell className="text-sm font-mono text-right font-semibold">
                          {isValid ? `₹${net.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="text-sm font-mono text-right">
                          {entry.lastBillDate ? (
                            (() => {
                              const days = Math.floor((new Date() - new Date(entry.lastBillDate)) / 86400000);
                              return <span className={days > 30 ? "text-red-600 font-semibold" : ""}>{days}d</span>;
                            })()
                          ) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
